import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, BookOpen, ListOrdered, Search, Home, Hand, CheckCircle2 } from "lucide-react";
import { PAGES, CHAPTERS } from "../data/bookContent";
import BookCover from "./BookCover";
import TableOfContents from "./TableOfContents";
import IndexPage from "./IndexPage";
import ChapterPage from "./ChapterPage";
import BackPage from "./BackPage";
import StickyNoteWidget from "./StickyNoteWidget";
import SynapseBackground from "./SynapseBackground";
import ShareBar from "./ShareBar";
import { AmcLogo } from "./BookCover";
import { isUnlocked, isChapterFree, markUnlocked, fetchPaymentStatus, pollPaymentStatus } from "../lib/payments";

const PROGRESS_KEY = "amc_last_page_v1";
const SWIPE_HINT_KEY = "amc_swipe_hint_seen_v1";

export default function ReaderLayout() {
  const [pageIndex, setPageIndex] = useState(() => {
    try {
      const raw = localStorage.getItem(PROGRESS_KEY);
      const idx = raw ? parseInt(raw, 10) : 0;
      return Number.isFinite(idx) && idx >= 0 && idx < PAGES.length ? idx : 0;
    } catch {
      return 0;
    }
  });
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [showSwipeHint, setShowSwipeHint] = useState(false);
  const [swipeFlash, setSwipeFlash] = useState(null); // {label, dir}
  const [unlocked, setUnlocked] = useState(() => isUnlocked());
  const [purchaseToast, setPurchaseToast] = useState(null); // {kind, label}

  // For double-swipe detection & remembering where user was before jumping to Index.
  const pendingSwipeRef = useRef(null); // { dir, timer }
  const prevPageBeforeIndexRef = useRef(null);

  useEffect(() => {
    try {
      if (!localStorage.getItem(SWIPE_HINT_KEY)) {
        setShowSwipeHint(true);
      }
    } catch {
      // ignore
    }
  }, []);

  // On mount: if Stripe redirected us back with ?session_id=..., poll status and unlock/tip-toast.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    // Owner bypass — AMC visits any deployment with ?owner=amc to unlock free forever on this browser.
    if (params.get("owner") === "amc") {
      markUnlocked();
      setUnlocked(true);
      setPurchaseToast({ kind: "unlock", label: "Owner mode — full access unlocked." });
      window.history.replaceState({}, "", window.location.pathname);
      setTimeout(() => setPurchaseToast(null), 4200);
      return;
    }

    const sessionId = params.get("session_id");
    const cancelled = params.get("payment") === "cancelled";
    if (cancelled) {
      setPurchaseToast({ kind: "cancelled", label: "Checkout cancelled — no charge made." });
      window.history.replaceState({}, "", window.location.pathname);
      setTimeout(() => setPurchaseToast(null), 4200);
      return;
    }
    if (!sessionId) return;
    let cancelledPoll = false;
    (async () => {
      const initial = await fetchPaymentStatus(sessionId).catch(() => null);
      let data = initial;
      if (!data || data.payment_status !== "paid") {
        data = await pollPaymentStatus(sessionId);
      }
      if (cancelledPoll) return;
      if (data && data.payment_status === "paid") {
        if (data.kind === "unlock") {
          markUnlocked();
          setUnlocked(true);
          setPurchaseToast({ kind: "unlock", label: "Full book unlocked. Enjoy!" });
        } else {
          setPurchaseToast({ kind: "tip", label: "Thanks for the coffee — you're a legend." });
        }
      } else {
        setPurchaseToast({ kind: "pending", label: "Payment still processing — refresh in a minute." });
      }
      window.history.replaceState({}, "", window.location.pathname);
      setTimeout(() => setPurchaseToast(null), 5200);
    })();
    return () => { cancelledPoll = true; };
  }, []);

  const dismissSwipeHint = () => {
    setShowSwipeHint(false);
    try { localStorage.setItem(SWIPE_HINT_KEY, "1"); } catch { /* ignore */ }
  };

  useEffect(() => {
    try {
      localStorage.setItem(PROGRESS_KEY, String(pageIndex));
    } catch {
      // ignore
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [pageIndex]);

  const currentPage = PAGES[pageIndex];
  const totalPages = PAGES.length;

  const jumpTo = useCallback((chapterOrKey) => {
    if (chapterOrKey === "__cover__") return setPageIndex(0);
    if (chapterOrKey === "__toc__") return setPageIndex(1);
    if (chapterOrKey === "__index__") {
      const i = PAGES.findIndex((p) => p.kind === "index");
      return setPageIndex(i);
    }
    const i = PAGES.findIndex((p) => p.kind === "chapter" && p.id === chapterOrKey);
    if (i !== -1) setPageIndex(i);
  }, []);

  const goPrev = useCallback(() => setPageIndex((i) => Math.max(0, i - 1)), []);
  const goNext = useCallback(() => setPageIndex((i) => Math.min(PAGES.length - 1, i + 1)), []);

  // Next/previous chapter (skips cover/toc/index/back — jumps between chapters only).
  const goNextChapter = useCallback(() => {
    setPageIndex((i) => {
      for (let j = i + 1; j < PAGES.length; j++) {
        if (PAGES[j].kind === "chapter") return j;
      }
      return i;
    });
  }, []);
  const goPrevChapter = useCallback(() => {
    setPageIndex((i) => {
      for (let j = i - 1; j >= 0; j--) {
        if (PAGES[j].kind === "chapter") return j;
      }
      return i;
    });
  }, []);

  const flashSwipe = useCallback((label) => {
    setSwipeFlash({ label, at: Date.now() });
    setTimeout(() => setSwipeFlash((s) => (s && Date.now() - s.at >= 550 ? null : s)), 600);
  }, []);

  // Core swipe dispatcher. Handles single vs double swipes for left/right with a 260ms window.
  const handleSwipe = useCallback(
    (dir) => {
      if (showSwipeHint) dismissSwipeHint();
      // Vertical: no double-gesture semantics — act instantly.
      if (dir === "up") {
        flashSwipe("Next chapter →");
        goNextChapter();
        return;
      }
      if (dir === "down") {
        flashSwipe("← Previous chapter");
        goPrevChapter();
        return;
      }

      // For left/right: buffer single swipe. If a matching second swipe arrives in the
      // window, cancel the single and perform the double action.
      const pending = pendingSwipeRef.current;
      if (pending && pending.dir === dir) {
        clearTimeout(pending.timer);
        pendingSwipeRef.current = null;
        if (dir === "right") {
          // Double right → jump to Index and remember where we came from.
          const idxPage = PAGES.findIndex((p) => p.kind === "index");
          if (idxPage !== -1) {
            setPageIndex((cur) => {
              if (PAGES[cur]?.kind !== "index") prevPageBeforeIndexRef.current = cur;
              return idxPage;
            });
            flashSwipe("⇥ Index");
          }
        } else if (dir === "left") {
          // Double left → return from Index to where we were.
          setPageIndex((cur) => {
            if (PAGES[cur]?.kind === "index" && prevPageBeforeIndexRef.current != null) {
              const target = prevPageBeforeIndexRef.current;
              prevPageBeforeIndexRef.current = null;
              flashSwipe("↩ Back to your page");
              return target;
            }
            // Fall through: behave as another "next page".
            flashSwipe("Next page →");
            return Math.min(PAGES.length - 1, cur + 1);
          });
        }
        return;
      }

      // Schedule the single action after the double-swipe window.
      const timer = setTimeout(() => {
        pendingSwipeRef.current = null;
        if (dir === "left") {
          flashSwipe("Next page →");
          goNext();
        } else if (dir === "right") {
          flashSwipe("← Previous page");
          goPrev();
        }
      }, 260);
      pendingSwipeRef.current = { dir, timer };
    },
    [goNext, goPrev, goNextChapter, goPrevChapter, flashSwipe, showSwipeHint]
  );

  // Bind touch swipe listeners.
  useEffect(() => {
    let startX = 0, startY = 0, lastX = 0, lastY = 0, startT = 0, tracking = false;
    const MIN_DIST_H = 45; // px horizontal
    const MIN_DIST_V = 90; // px vertical (higher to avoid conflict with scroll)
    const MAX_TIME_H = 700; // ms
    const MAX_TIME_V = 500; // ms (must be a decisive flick)

    const onStart = (e) => {
      if (e.touches.length !== 1) { tracking = false; return; }
      const t = e.target;
      if (t && t.closest && (t.closest(".sticky-note") || t.closest("input") || t.closest("textarea") || t.closest("button") || t.closest("a"))) {
        tracking = false;
        return;
      }
      const p = e.touches[0];
      startX = lastX = p.clientX;
      startY = lastY = p.clientY;
      startT = Date.now();
      tracking = true;
    };
    const onMove = (e) => {
      if (!tracking || e.touches.length !== 1) return;
      const p = e.touches[0];
      lastX = p.clientX;
      lastY = p.clientY;
    };
    const onEnd = () => {
      if (!tracking) return;
      tracking = false;
      const dx = lastX - startX;
      const dy = lastY - startY;
      const dt = Date.now() - startT;
      const absX = Math.abs(dx), absY = Math.abs(dy);
      if (absX > absY) {
        if (absX >= MIN_DIST_H && dt <= MAX_TIME_H) {
          handleSwipe(dx < 0 ? "left" : "right");
        }
      } else {
        if (absY >= MIN_DIST_V && dt <= MAX_TIME_V) {
          handleSwipe(dy < 0 ? "up" : "down");
        }
      }
    };
    window.addEventListener("touchstart", onStart, { passive: true });
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("touchend", onEnd, { passive: true });
    window.addEventListener("touchcancel", onEnd, { passive: true });
    return () => {
      window.removeEventListener("touchstart", onStart);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onEnd);
      window.removeEventListener("touchcancel", onEnd);
    };
  }, [handleSwipe]);

  // Bind trackpad (two-finger) wheel listener.
  // Vertical wheel is ALWAYS left as normal scroll — so reading long chapters is comfortable.
  // Only horizontal two-finger swipes control the book (browsers can't reliably tell
  // 2-finger from 3-finger, so we use direction instead: horizontal = command, vertical = scroll).
  useEffect(() => {
    let accumX = 0, lastTs = 0, coolUntil = 0;
    const H_THRESHOLD = 120;      // px accumulated to trigger a horizontal page swipe (higher = less sensitive)
    const RESET_MS = 220;
    const COOLDOWN_MS = 600;

    const onWheel = (e) => {
      const now = Date.now();
      if (now < coolUntil) {
        if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) e.preventDefault();
        return;
      }
      // Only react to horizontal-dominant gestures. Vertical always scrolls normally.
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY) * 1.4) {
        accumX = 0;
        return;
      }
      if (now - lastTs > RESET_MS) accumX = 0;
      lastTs = now;
      accumX += e.deltaX;
      if (Math.abs(accumX) >= H_THRESHOLD) {
        e.preventDefault();
        const dir = accumX < 0 ? "right" : "left";
        handleSwipe(dir);
        accumX = 0;
        coolUntil = now + COOLDOWN_MS;
      }
    };

    window.addEventListener("wheel", onWheel, { passive: false });
    return () => window.removeEventListener("wheel", onWheel);
  }, [handleSwipe]);

  useEffect(() => {
    const handler = (e) => {
      if (e.target && (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA")) return;
      if (e.key === "ArrowRight") goNext();
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowDown") goNextChapter();
      if (e.key === "ArrowUp") goPrevChapter();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [goNext, goPrev, goNextChapter, goPrevChapter]);

  const filteredChapters = useMemo(() => {
    const q = searchQ.trim().toLowerCase();
    if (!q) return CHAPTERS;
    return CHAPTERS.filter(
      (c) =>
        c.title.toLowerCase().includes(q) ||
        c.summary.toLowerCase().includes(q) ||
        c.content.toLowerCase().includes(q)
    );
  }, [searchQ]);

  const progress = ((pageIndex + 1) / totalPages) * 100;

  return (
    <div className="relative min-h-screen z-[2]">
      <SynapseBackground intensity={0.55} />

      {/* Purchase / cancel toast */}
      <AnimatePresence>
        {purchaseToast && (
          <motion.div
            key={purchaseToast.label}
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.3 }}
            className="fixed top-20 left-1/2 -translate-x-1/2 z-[85] max-w-md w-[92%] px-4 py-3 rounded-xl bg-slate-900/95 border border-cyan-400/50 backdrop-blur flex items-center gap-3"
            style={{ boxShadow: "0 0 30px rgba(0,240,255,0.35)" }}
            data-testid="purchase-toast"
          >
            <CheckCircle2 size={20} className="text-cyan-300 shrink-0" />
            <div className="text-sm text-slate-100 font-heading">{purchaseToast.label}</div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Swipe direction flash toast */}
      <AnimatePresence>
        {swipeFlash && (
          <motion.div
            key={swipeFlash.at}
            initial={{ opacity: 0, y: 12, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.95 }}
            transition={{ duration: 0.25 }}
            className="fixed top-24 left-1/2 -translate-x-1/2 z-[80] pointer-events-none px-5 py-2.5 rounded-full bg-slate-900/90 border border-cyan-400/40 text-cyan-100 text-sm font-heading tracking-wide"
            style={{ boxShadow: "0 0 24px rgba(0,240,255,0.35)" }}
            data-testid="swipe-flash"
          >
            {swipeFlash.label}
          </motion.div>
        )}
      </AnimatePresence>

      {/* First-visit swipe hint */}
      <AnimatePresence>
        {showSwipeHint && (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 24 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-x-0 bottom-24 z-[75] flex justify-center px-4 pointer-events-none"
            data-testid="swipe-hint"
          >
            <div className="pointer-events-auto max-w-md w-full bg-slate-900/95 border border-cyan-400/30 rounded-2xl px-5 py-4 backdrop-blur-lg" style={{ boxShadow: "0 10px 40px rgba(0,240,255,0.15)" }}>
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-full bg-cyan-400/15 border border-cyan-400/40 flex items-center justify-center text-cyan-300 shrink-0">
                  <Hand size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-semibold text-slate-100 text-sm mb-1">
                    This book responds to swipes
                    <span className="text-slate-400 font-normal"> — or two-finger trackpad</span>
                  </div>
                  <ul className="text-xs text-slate-300 space-y-0.5 font-body leading-relaxed">
                    <li><span className="text-cyan-300 font-mono">← swipe</span> next page &nbsp;·&nbsp; <span className="text-cyan-300 font-mono">swipe →</span> previous page</li>
                    <li><span className="text-cyan-300 font-mono">↑ swipe</span> next chapter &nbsp;·&nbsp; <span className="text-cyan-300 font-mono">swipe ↓</span> previous chapter</li>
                    <li><span className="text-magenta-300 font-mono" style={{color:'#FF8CB0'}}>swipe → →</span> jump to Index &nbsp;·&nbsp; <span className="text-magenta-300 font-mono" style={{color:'#FF8CB0'}}>← ← swipe</span> back from Index</li>
                  </ul>
                </div>
                <button
                  onClick={dismissSwipeHint}
                  data-testid="swipe-hint-dismiss"
                  className="text-slate-400 hover:text-cyan-200 text-xs font-heading uppercase tracking-widest shrink-0"
                >
                  Got it
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Top bar */}
      <header className="sticky top-0 z-40 backdrop-blur-lg bg-slate-950/70 border-b border-cyan-400/10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <button
            onClick={() => jumpTo("__cover__")}
            data-testid="header-home-btn"
            className="flex items-center gap-3 group"
          >
            <AmcLogo size={34} />
            <div className="hidden sm:block leading-tight text-left">
              <div className="font-display font-extrabold text-white text-sm">
                AMC&apos;S ABC&apos;S of AI
              </div>
              <div className="font-heading text-[10px] uppercase tracking-[0.25em] text-cyan-300/70">
                AMC inNOVAtions
              </div>
            </div>
          </button>

          <div className="flex items-center gap-1 sm:gap-2">
            <button
              onClick={() => jumpTo("__toc__")}
              data-testid="header-contents-btn"
              className="px-3 h-9 rounded-full text-slate-300 hover:text-cyan-200 hover:bg-cyan-400/10 flex items-center gap-1.5 text-sm font-heading"
              title="Contents"
            >
              <ListOrdered size={16} />
              <span className="hidden sm:inline">Contents</span>
            </button>
            <button
              onClick={() => jumpTo("__index__")}
              data-testid="header-index-btn"
              className="px-3 h-9 rounded-full text-slate-300 hover:text-cyan-200 hover:bg-cyan-400/10 flex items-center gap-1.5 text-sm font-heading"
              title="Index"
            >
              <BookOpen size={16} />
              <span className="hidden sm:inline">Index</span>
            </button>
            <button
              onClick={() => setSearchOpen((s) => !s)}
              data-testid="header-search-btn"
              className="px-3 h-9 rounded-full text-slate-300 hover:text-cyan-200 hover:bg-cyan-400/10 flex items-center gap-1.5 text-sm font-heading"
              title="Search"
            >
              <Search size={16} />
              <span className="hidden sm:inline">Search</span>
            </button>
          </div>
        </div>
        {/* Reading progress bar */}
        <div className="h-[2px] bg-slate-800/60">
          <motion.div
            className="h-full bg-gradient-to-r from-cyan-400 to-violet-500"
            initial={false}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            data-testid="progress-bar"
          />
        </div>
      </header>

      {/* Search dropdown */}
      <AnimatePresence>
        {searchOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="sticky top-16 z-30 bg-slate-950/95 backdrop-blur border-b border-cyan-400/10"
          >
            <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4">
              <input
                autoFocus
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                placeholder="Search chapters — try 'debugging', 'github', 'money'…"
                data-testid="search-input"
                className="w-full h-11 rounded-lg bg-slate-900/70 border border-slate-700 focus:border-cyan-400 focus:outline-none px-4 text-slate-100 placeholder:text-slate-500 font-body"
              />
              {searchQ && (
                <div className="mt-3 max-h-72 overflow-auto rounded-lg border border-slate-800 divide-y divide-slate-800">
                  {filteredChapters.length === 0 && (
                    <div className="p-4 text-sm text-slate-400" data-testid="search-empty">No matches. Try another word.</div>
                  )}
                  {filteredChapters.map((c) => (
                    <button
                      key={c.id}
                      onClick={() => {
                        jumpTo(c.id);
                        setSearchOpen(false);
                        setSearchQ("");
                      }}
                      data-testid={`search-result-${c.id}`}
                      className="w-full text-left p-3 hover:bg-cyan-400/5"
                    >
                      <div className="font-heading text-slate-100 text-sm">{c.title}</div>
                      <div className="text-slate-400 text-xs mt-0.5">{c.summary}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Page */}
      <main className="relative pt-8 sm:pt-12 pb-40 px-3 sm:px-6">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentPage.id}
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -30 }}
            transition={{ duration: 0.35, ease: "easeOut" }}
          >
            {currentPage.kind === "cover" && (
              <BookCover onOpen={() => jumpTo("__toc__")} />
            )}
            {currentPage.kind === "toc" && (
              <TableOfContents onJump={jumpTo} currentPageIndex={pageIndex} />
            )}
            {currentPage.kind === "chapter" && (
              <ChapterPage
                chapter={currentPage.chapter}
                pageIndex={pageIndex}
                totalPages={totalPages}
                locked={!unlocked && !isChapterFree(currentPage.chapter.id)}
              />
            )}
            {currentPage.kind === "index" && <IndexPage onJump={jumpTo} />}
            {currentPage.kind === "back" && (
              <BackPage onGoContents={() => jumpTo("__toc__")} />
            )}
          </motion.div>
        </AnimatePresence>
      </main>

      {/* Bottom nav bar */}
      <nav className="fixed bottom-0 left-0 right-0 z-40 border-t border-cyan-400/15 bg-slate-950/85 backdrop-blur-lg">
        <div className="max-w-5xl mx-auto px-3 sm:px-6 h-16 flex items-center justify-between gap-2">
          <button
            onClick={goPrev}
            disabled={pageIndex === 0}
            data-testid="nav-prev-btn"
            className="flex items-center gap-1.5 h-10 px-4 rounded-full text-slate-200 hover:text-cyan-200 hover:bg-cyan-400/10 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-200 font-heading text-sm"
          >
            <ChevronLeft size={18} /> <span className="hidden sm:inline">Previous</span>
          </button>

          <div className="text-center min-w-0 flex-1 px-2">
            <div className="text-[10px] uppercase tracking-widest text-cyan-300/70 font-heading" data-testid="page-kind-label">
              {currentPage.kind === "chapter"
                ? currentPage.chapter.number
                : currentPage.kind === "toc"
                ? "Contents"
                : currentPage.kind === "index"
                ? "Index"
                : currentPage.kind === "cover"
                ? "Cover"
                : "Fin"}
            </div>
            <div className="truncate text-sm text-slate-200 font-heading" data-testid="page-title-label">
              {currentPage.kind === "chapter" ? currentPage.chapter.title : currentPage.title}
            </div>
          </div>

          <button
            onClick={goNext}
            disabled={pageIndex === PAGES.length - 1}
            data-testid="nav-next-btn"
            className="flex items-center gap-1.5 h-10 px-4 rounded-full text-slate-200 hover:text-cyan-200 hover:bg-cyan-400/10 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-200 font-heading text-sm"
          >
            <span className="hidden sm:inline">Next</span> <ChevronRight size={18} />
          </button>
        </div>
      </nav>

      {/* Sticky notes — only on non-cover pages */}
      {currentPage.kind !== "cover" && (
        <StickyNoteWidget pageId={currentPage.id} />
      )}

      {/* Social share bar — only on chapter pages */}
      {currentPage.kind === "chapter" && (
        <ShareBar chapter={currentPage.chapter} />
      )}
    </div>
  );
}
